import React, { useEffect, useState } from 'react';
import './App.css';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { api } from './services/api';
import { useRemoteData } from './hooks/useRemoteData';
import { DashboardPage } from './pages/DashboardPage';
import { ChampionshipsPage } from './pages/ChampionshipsPage';
import { TeamsPage } from './pages/TeamsPage';
import { PlayersPage } from './pages/PlayersPage';
import { MatchesPage } from './pages/MatchesPage';
import { FieldsPage } from './pages/FieldsPage';
import { OfficiatingPage } from './pages/OfficiatingPage';
import { TransmissionsPage } from './pages/TransmissionsPage';
import { RankingPage } from './pages/RankingPage';
import { StatisticsPage } from './pages/StatisticsPage';
import { ProductsPage } from './pages/ProductsPage';
import { CouponsPage } from './pages/CouponsPage';
import { OrdersPage } from './pages/OrdersPage';
import { ChatPage } from './pages/ChatPage';
import { JournalPage } from './pages/JournalPage';
import { ModerationPage } from './pages/ModerationPage';
import { UsersPage } from './pages/UsersPage';
import { SmsAlertsPage } from './pages/SmsAlertsPage';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { PerifaNotFoundGame } from './components/PerifaNotFoundGame';
import { navigationItems } from './data/navigation';
import perifaAppIcon from './assets/perifa-pro-app-icon.png';

const AUTH_STORAGE_KEY = 'perifa_pro_admin_token';

function readStoredToken() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  return sessionStorage.getItem(AUTH_STORAGE_KEY) || '';
}

export function resolveRouteMode(pathname = '/', hash = '') {
  const path = String(pathname || '/').replace(/\/+$/, '') || '/';
  const normalizedHash = String(hash || '').toLowerCase();

  if (normalizedHash === '#publico' || normalizedHash === '#lgpd') {
    return 'public';
  }
  if (normalizedHash === '#admin') {
    return 'app';
  }

  if (hash && hash !== '#') {
    return 'not-found';
  }

  if (path === '/' || path === '/index.html') {
    return 'public';
  }

  if (path === '/admin' || path === '/login') {
    return 'app';
  }

  return 'not-found';
}

function readRouteMode() {
  return resolveRouteMode(window.location.pathname, window.location.hash);
}

function pickDefaultChampionshipId(items = []) {
  if (!items.length) {
    return null;
  }

  const sorted = [...items].sort((left, right) => {
    const leftPriority = championshipPriority(left.status);
    const rightPriority = championshipPriority(right.status);
    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    const leftDate = left.start_date || '';
    const rightDate = right.start_date || '';
    return String(rightDate).localeCompare(String(leftDate));
  });

  return sorted[0]?.id ? Number(sorted[0].id) : null;
}

function championshipPriority(status) {
  switch (status) {
    case 'running':
      return 0;
    case 'published':
      return 1;
    case 'draft':
      return 2;
    case 'finished':
      return 3;
    case 'cancelled':
      return 4;
    default:
      return 5;
  }
}

export function shouldLoadSmsAlerts({ isAuthenticated, currentView, roles = [] }) {
  const canManageSmsAlerts = roles.some((role) => role === 'super_admin' || role === 'admin' || role === 'organizer');
  return Boolean(isAuthenticated && currentView === 'smsAlerts' && canManageSmsAlerts);
}

function ageFromBirthDate(value) {
  if (!value) return null;
  const birth = new Date(`${value}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

const PUBLIC_ACCOUNT_OPTIONS = [
  { value: 'torcedor', label: 'Conta comum / usuário' },
];

const EMPTY_LOGIN_FORM = {
  email: '',
  password: '',
};

const EMPTY_PASSWORD_RESET_FORM = {
  email: '',
  code: '',
  new_password: '',
  confirm_password: '',
};

const PAGE_META = {
  dashboard: { title: 'Central da Rodada', description: 'Hub vivo da liga com jogo em destaque, transmissão, comunidade, ranking e operação.' },
  championships: { title: 'Campeonatos', description: 'Camada administrativa da temporada, formato e janelas do torneio.' },
  teams: { title: 'Times', description: 'Base de clubes, identidade, cidade e responsáveis.' },
  players: { title: 'Jogadores', description: 'Elenco consolidado com leitura esportiva e status físico.' },
  matches: { title: 'Jogos', description: 'Central de partida com placar, eventos, transmissão e encerramento técnico.' },
  smsAlerts: { title: 'Alertas SMS', description: 'Disparo controlado de lembretes e avisos relevantes por tenant, jogo, time ou campeonato.' },
  fields: { title: 'Campos', description: 'Infraestrutura oficial da competição, com cadastro, edição e remoção de locais.' },
  officiating: { title: 'Arbitragem', description: 'Escala oficial de árbitros, assistentes e operação técnica da rodada.' },
  transmissions: { title: 'Transmissões', description: 'Gestão centralizada de vídeos ao vivo, embed, replay e miniaturas.' },
  ranking: { title: 'Ranking', description: 'Classificação atual do campeonato em operação.' },
  statistics: { title: 'Estatísticas', description: 'Leitura consolidada por jogador e time, alimentada pelo motor de partida.' },
  products: { title: 'Loja', description: 'Catálogo, estoque e cadastro de itens do marketplace.' },
  coupons: { title: 'Cupons', description: 'Campanhas de desconto com validade, limites e controle de uso.' },
  orders: { title: 'Pedidos', description: 'Pós-venda, status logístico, itens e histórico operacional.' },
  chat: { title: 'Arquibancada Viva', description: 'Camada social da plataforma: torcida, jogo, times, avisos oficiais e operação.' },
  journal: { title: 'Jornal Perifa', description: 'Materias, bastidores, entrevistas e enquetes organizadas por campinho.' },
  moderation: { title: 'Moderação', description: 'Mute, ban e revisão das salas da comunidade e operação.' },
  users: { title: 'Usuários', description: 'Base de contas, permissões efetivas e trilha de auditoria.' },
};

function accessKeyForView(view) {
  return navigationItems.find((item) => item.id === view)?.accessKey || view;
}

function LoginScreen({ onSubmit, onRegister, onForgotPassword, onResetPassword, loading, error }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState(EMPTY_LOGIN_FORM);
  const [passwordResetForm, setPasswordResetForm] = useState(EMPTY_PASSWORD_RESET_FORM);
  const [passwordResetState, setPasswordResetState] = useState({ loading: false, error: '', success: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    birth_date: '',
    guardian_name: '',
    guardian_contact: '',
    guardian_document: '',
    guardian_consent: false,
    password: '',
    confirm_password: '',
    account_type: 'torcedor',
  });
  const [registerState, setRegisterState] = useState({ loading: false, error: '', success: '' });
  const registerAge = ageFromBirthDate(registerForm.birth_date);
  const registerNeedsGuardian = registerAge !== null && registerAge >= 16 && registerAge < 18;
  const registerUnderMinimumAge = registerAge !== null && registerAge < 16;
  const isPasswordResetMode = mode === 'forgot' || mode === 'reset';
  const isBusy = loading || registerState.loading || passwordResetState.loading;
  const resetAuthModeState = () => {
    setRegisterState({ loading: false, error: '', success: '' });
    setPasswordResetState({ loading: false, error: '', success: '' });
  };

  return (
    <div className="login-shell">
      <div className="login-hero">
        <span className="eyebrow">Perifa Pro</span>
        <h1>Central da liga, operação e arquibancada</h1>
        <p>
          Controle competição, elenco, partidas, loja, pedidos e comunidade como um ecossistema esportivo vivo.
        </p>
        <a className="inline-button public-link" href="/">
          Ver apresentação pública
        </a>
      </div>
      <form className="login-card" autoComplete="off" onSubmit={(event) => {
        event.preventDefault();
        if (mode === 'login') {
          onSubmit(form);
          return;
        }

        if (mode === 'forgot') {
          setPasswordResetState({ loading: true, error: '', success: '' });
          Promise.resolve(onForgotPassword({ email: passwordResetForm.email.trim().toLowerCase() }))
            .then(() => {
              setPasswordResetState({
                loading: false,
                error: '',
                success: 'Se o email estiver cadastrado, enviaremos um código de recuperação.',
              });
              setMode('reset');
            })
            .catch((forgotError) => {
              setPasswordResetState({
                loading: false,
                error: forgotError?.message || 'Falha ao solicitar recuperação.',
                success: '',
              });
            });
          return;
        }

        if (mode === 'reset') {
          if (passwordResetForm.new_password !== passwordResetForm.confirm_password) {
            setPasswordResetState({
              loading: false,
              error: 'A confirmação da nova senha não confere.',
              success: '',
            });
            return;
          }

          setPasswordResetState({ loading: true, error: '', success: '' });
          Promise.resolve(onResetPassword({
            email: passwordResetForm.email.trim().toLowerCase(),
            code: passwordResetForm.code.trim(),
            new_password: passwordResetForm.new_password,
            confirm_password: passwordResetForm.confirm_password,
          }))
            .then(() => {
              setPasswordResetState({ loading: false, error: '', success: 'Senha redefinida com sucesso. Faça login.' });
              setForm({ email: passwordResetForm.email.trim().toLowerCase(), password: '' });
              setPasswordResetForm(EMPTY_PASSWORD_RESET_FORM);
              setMode('login');
            })
            .catch((resetError) => {
              setPasswordResetState({
                loading: false,
                error: resetError?.message || 'Falha ao redefinir senha.',
                success: '',
              });
            });
          return;
        }

        if (registerForm.password !== registerForm.confirm_password) {
          setRegisterState({
            loading: false,
            error: 'As senhas não conferem.',
            success: '',
          });
          return;
        }

        if (registerUnderMinimumAge) {
          setRegisterState({
            loading: false,
            error: 'Cadastro de conta permitido somente a partir de 16 anos.',
            success: '',
          });
          return;
        }

        setRegisterState({ loading: true, error: '', success: '' });
        Promise.resolve(onRegister({
          ...registerForm,
          name: registerForm.name.trim(),
          email: registerForm.email.trim().toLowerCase(),
          phone: registerForm.phone.trim(),
          account_type: 'torcedor',
        }))
          .then(() => {
            setRegisterState({ loading: false, error: '', success: 'Conta criada com sucesso. Agora faça login.' });
            setMode('login');
            setForm({ email: registerForm.email, password: '' });
            setRegisterForm({
              name: '',
              email: '',
              phone: '',
              birth_date: '',
              guardian_name: '',
              guardian_contact: '',
              guardian_document: '',
              guardian_consent: false,
              password: '',
              confirm_password: '',
              account_type: 'torcedor',
            });
          })
          .catch((registerError) => {
            setRegisterState({
              loading: false,
              error: registerError?.message || 'Falha ao criar conta.',
              success: '',
            });
          });
      }}>
        <div className="login-card__brand">
          <img src={perifaAppIcon} alt="" aria-hidden="true" />
          <div>
            <span className="eyebrow">Perifa Pro</span>
            <h2>
              {mode === 'login'
                ? 'Entrar na sua conta'
                : mode === 'register'
                  ? 'Criar conta nova'
                  : 'Recuperar senha'}
            </h2>
            <p>
              {isPasswordResetMode
                ? 'Receba o código e defina uma nova senha de acesso.'
                : 'Campeonato, transmissão e comunidade com visual premium.'}
            </p>
          </div>
        </div>
        {mode === 'login' ? (
          <>
            <label>
              Email
              <input
                name="perifa_login_email"
                type="email"
                autoComplete="username"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </label>
            <label>
              Senha
              <input
                name="perifa_login_password"
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </label>
          </>
        ) : isPasswordResetMode ? (
          <>
            <label>
              Email
              <input
                name="perifa_recovery_email"
                type="email"
                autoComplete="email"
                value={passwordResetForm.email}
                onChange={(e) => setPasswordResetForm({ ...passwordResetForm, email: e.target.value })}
                required
              />
            </label>
            {mode === 'reset' ? (
              <>
                <label>
                  Código de recuperação
                  <input
                    name="perifa_recovery_code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={passwordResetForm.code}
                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, code: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Nova senha
                  <input
                    name="perifa_recovery_new_password"
                    type="password"
                    autoComplete="new-password"
                    value={passwordResetForm.new_password}
                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, new_password: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Confirmar nova senha
                  <input
                    name="perifa_recovery_confirm_password"
                    type="password"
                    autoComplete="new-password"
                    value={passwordResetForm.confirm_password}
                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, confirm_password: e.target.value })}
                    required
                  />
                </label>
              </>
            ) : null}
          </>
        ) : (
          <>
            <label>
              Nome
              <input name="perifa_register_name" autoComplete="name" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required />
            </label>
            <label>
              Email
              <input name="perifa_register_email" type="email" autoComplete="email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required />
            </label>
            <label>
              Telefone
              <input name="perifa_register_phone" type="tel" autoComplete="tel" value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })} />
            </label>
            <label>
              Data de nascimento
              <input name="perifa_register_birth_date" type="date" autoComplete="bday" value={registerForm.birth_date} onChange={(e) => setRegisterForm({ ...registerForm, birth_date: e.target.value })} required />
            </label>
            {registerUnderMinimumAge ? <div className="page-error small">Contas no app são permitidas somente a partir de 16 anos.</div> : null}
            {registerNeedsGuardian ? (
              <>
                <label>
                  Responsável legal
                  <input name="perifa_register_guardian_name" autoComplete="off" value={registerForm.guardian_name} onChange={(e) => setRegisterForm({ ...registerForm, guardian_name: e.target.value })} required />
                </label>
                <label>
                  Contato do responsável
                  <input name="perifa_register_guardian_contact" autoComplete="off" value={registerForm.guardian_contact} onChange={(e) => setRegisterForm({ ...registerForm, guardian_contact: e.target.value })} required />
                </label>
                <label>
                  Documento do responsável
                  <input name="perifa_register_guardian_document" autoComplete="off" value={registerForm.guardian_document} onChange={(e) => setRegisterForm({ ...registerForm, guardian_document: e.target.value })} />
                </label>
                <label className="checkbox-field">
                  <span>Tenho autorização do responsável legal</span>
                  <input type="checkbox" checked={Boolean(registerForm.guardian_consent)} onChange={(e) => setRegisterForm({ ...registerForm, guardian_consent: e.target.checked })} required />
                </label>
              </>
            ) : null}
            <div className="success-note">{PUBLIC_ACCOUNT_OPTIONS[0].label}</div>
            <label>
              Senha
              <input name="perifa_register_password" type="password" autoComplete="new-password" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required />
            </label>
            <label>
              Confirmar senha
              <input name="perifa_register_confirm_password" type="password" autoComplete="new-password" value={registerForm.confirm_password} onChange={(e) => setRegisterForm({ ...registerForm, confirm_password: e.target.value })} required />
            </label>
          </>
        )}
        {mode === 'login' && error ? <div className="page-error small">{error}</div> : null}
        {mode === 'register' && registerState.error ? <div className="page-error small">{registerState.error}</div> : null}
        {isPasswordResetMode && passwordResetState.error ? <div className="page-error small">{passwordResetState.error}</div> : null}
        {registerState.success ? <div className="success-note">{registerState.success}</div> : null}
        {passwordResetState.success ? <div className="success-note">{passwordResetState.success}</div> : null}
        {mode === 'login' ? (
          <button
            className="text-button"
            type="button"
            onClick={() => {
              resetAuthModeState();
              setPasswordResetForm({ ...EMPTY_PASSWORD_RESET_FORM, email: form.email.trim().toLowerCase() });
              setMode('forgot');
            }}
            disabled={isBusy}
          >
            Esqueci minha senha
          </button>
        ) : null}
        <button className="primary-button" type="submit" disabled={isBusy}>
          {mode === 'login'
            ? (loading ? 'Autenticando...' : 'Entrar no painel')
            : mode === 'register'
              ? (registerState.loading ? 'Criando...' : 'Criar conta')
              : mode === 'forgot'
                ? (passwordResetState.loading ? 'Enviando...' : 'Enviar código')
                : (passwordResetState.loading ? 'Salvando...' : 'Redefinir senha')}
        </button>
        <button
          className="ghost-button"
          type="button"
          onClick={() => {
            resetAuthModeState();
            setMode(mode === 'login' ? 'register' : 'login');
          }}
          disabled={isBusy}
        >
          {mode === 'login' ? 'Criar conta' : 'Voltar para login'}
        </button>
      </form>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(readStoredToken());
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(Boolean(readStoredToken()));
  const [authError, setAuthError] = useState('');
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedModerationRoomId, setSelectedModerationRoomId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedChampionshipId, setSelectedChampionshipId] = useState(null);
  const [productFilters, setProductFilters] = useState({ status: 'all', team_id: '', category_id: '' });
  const [couponFilters, setCouponFilters] = useState({ status: 'all' });
  const [orderFilters, setOrderFilters] = useState({ status: 'all', payment_status: 'all' });
  const [busyAction, setBusyAction] = useState('');
  const [routeMode, setRouteMode] = useState(readRouteMode);
  const isAuthenticated = Boolean(token && user);
  const shouldLoadAppData = isAuthenticated && routeMode === 'app';
  const access = user?.access || {};

  useEffect(() => {
    const handleRouteChange = () => setRouteMode(readRouteMode());

    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
  const manageableRoles = user?.roles?.includes('super_admin')
    ? ['organizer', 'team_owner', 'player', 'referee', 'fan', 'store']
    : user?.roles?.includes('admin')
      ? ['organizer', 'team_owner', 'player', 'referee', 'fan', 'store']
      : ['team_owner', 'player', 'referee', 'fan', 'store'];

  const dashboard = useRemoteData(() => api.dashboard(token), [token, routeMode], shouldLoadAppData);
  const championships = useRemoteData(() => api.championships(token), [token, routeMode], shouldLoadAppData);
  const teams = useRemoteData(() => api.teams(token), [token, routeMode], shouldLoadAppData);
  const players = useRemoteData(() => api.players(token), [token, routeMode], shouldLoadAppData);
  const fields = useRemoteData(() => api.fields(token), [token, routeMode], shouldLoadAppData);
  const matches = useRemoteData(() => api.matches(token), [token, routeMode], shouldLoadAppData);
  const matchDetail = useRemoteData(
    () => (selectedMatchId ? api.matchDetail(token, selectedMatchId) : Promise.resolve({ data: null })),
    [token, routeMode, selectedMatchId],
    shouldLoadAppData && Boolean(selectedMatchId),
  );
  const ranking = useRemoteData(() => api.ranking(token, selectedChampionshipId), [token, routeMode, selectedChampionshipId], shouldLoadAppData);
  const playerStats = useRemoteData(() => api.playerStats(token, selectedChampionshipId), [token, routeMode, selectedChampionshipId], shouldLoadAppData);
  const teamStats = useRemoteData(() => api.teamStats(token, selectedChampionshipId), [token, routeMode, selectedChampionshipId], shouldLoadAppData);
  const products = useRemoteData(() => api.products(token, productFilters), [token, routeMode, productFilters.status, productFilters.team_id, productFilters.category_id], shouldLoadAppData);
  const marketplace = useRemoteData(() => api.marketplaceStatus(token), [token, routeMode], shouldLoadAppData && Boolean(access.products?.view));
  const coupons = useRemoteData(() => api.coupons(token, couponFilters), [token, routeMode, couponFilters.status], shouldLoadAppData);
  const orders = useRemoteData(() => api.orders(token, orderFilters), [token, routeMode, orderFilters.status, orderFilters.payment_status], shouldLoadAppData);
  const orderDetail = useRemoteData(
    () => (selectedOrderId ? api.orderDetail(token, selectedOrderId) : Promise.resolve({ data: null })),
    [token, routeMode, selectedOrderId],
    shouldLoadAppData && Boolean(selectedOrderId),
  );
  const rooms = useRemoteData(() => api.chatRooms(token), [token, routeMode], shouldLoadAppData);
  const journal = useRemoteData(() => api.journalPosts(token), [token, routeMode], shouldLoadAppData);
  const roomMessages = useRemoteData(
    () => (selectedRoomId ? api.chatMessages(token, selectedRoomId) : Promise.resolve({ data: { items: [] } })),
    [token, routeMode, selectedRoomId],
    shouldLoadAppData && Boolean(selectedRoomId),
  );
  const moderation = useRemoteData(
    () => (selectedModerationRoomId ? api.chatModeration(token, selectedModerationRoomId) : Promise.resolve({ data: { items: [] } })),
    [token, routeMode, selectedModerationRoomId],
    shouldLoadAppData && Boolean(selectedModerationRoomId),
  );
  const users = useRemoteData(() => api.users(token), [token, routeMode], shouldLoadAppData);
  const auditLogs = useRemoteData(() => api.auditLogs(token, { limit: 80 }), [token, routeMode], shouldLoadAppData);
  const smsAlerts = useRemoteData(
    () => api.smsAlerts(token),
    [token, currentView, user?.roles?.join('|')],
    shouldLoadSmsAlerts({ isAuthenticated: shouldLoadAppData, currentView, roles: user?.roles || [] }),
  );

  useEffect(() => {
    if (!token) {
      setUser(null);
      setAuthLoading(false);
      return;
    }

    let active = true;
    setAuthLoading(true);
    api.me(token)
      .then((response) => {
        if (!active) return;
        setUser(response.data);
        setAuthError('');
      })
      .catch((error) => {
        if (!active) return;
        setAuthError(error.message || 'Sessão inválida.');
        sessionStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setToken('');
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!selectedRoomId && rooms.data?.data?.items?.length) {
      setSelectedRoomId(rooms.data.data.items[0].id);
    }
  }, [rooms.data, selectedRoomId]);

  useEffect(() => {
    if (!selectedModerationRoomId && rooms.data?.data?.items?.length) {
      setSelectedModerationRoomId(rooms.data.data.items[0].id);
    }
  }, [rooms.data, selectedModerationRoomId]);

  useEffect(() => {
    const items = championships.data?.data?.items || [];
    if (!items.length) {
      if (selectedChampionshipId !== null) {
        setSelectedChampionshipId(null);
      }
      return;
    }

    if (selectedChampionshipId !== null && items.some((item) => Number(item.id) === Number(selectedChampionshipId))) {
      return;
    }

    const nextId = pickDefaultChampionshipId(items);
    if (nextId !== null && nextId !== selectedChampionshipId) {
      setSelectedChampionshipId(nextId);
    }
  }, [championships.data, selectedChampionshipId]);

  const pageMeta = PAGE_META[currentView] || PAGE_META.dashboard;

  useEffect(() => {
    if (!user?.access) {
      return;
    }

    const canViewCurrent = access[accessKeyForView(currentView)]?.view;
    if (!canViewCurrent) {
      const fallback = Object.keys(PAGE_META).find((key) => access[accessKeyForView(key)]?.view) || 'dashboard';
      if (fallback !== currentView) {
        setCurrentView(fallback);
      }
    }
  }, [access, currentView, user]);

  const login = async (credentials) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await api.login(credentials);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      sessionStorage.setItem(AUTH_STORAGE_KEY, response.data.token);
      setToken(response.data.token);
      setUser(response.data.user || null);
    } catch (error) {
      setAuthError(error.message || 'Falha de autenticação.');
    } finally {
      setAuthLoading(false);
    }
  };

  const register = async (payload) => {
    const response = await api.register(payload);
    return response.data;
  };

  const forgotPassword = async (payload) => {
    const response = await api.forgotPassword(payload);
    return response.data;
  };

  const resetPassword = async (payload) => {
    const response = await api.resetPassword(payload);
    return response.data;
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setToken('');
    setUser(null);
    setSelectedChampionshipId(null);
    setSelectedRoomId(null);
    setSelectedModerationRoomId(null);
    setSelectedOrderId(null);
    setSelectedMatchId(null);
  };

  const syncSportsViews = async () => {
    await Promise.all([
      dashboard.reload(),
      fields.reload(),
      matches.reload(),
      matchDetail.reload(),
      ranking.reload(),
      playerStats.reload(),
      teamStats.reload(),
    ]);
  };

  const refreshCurrentPage = () => {
    const loaders = {
      dashboard: dashboard.reload,
      championships: championships.reload,
      teams: teams.reload,
      players: players.reload,
      matches: () => Promise.all([matches.reload(), matchDetail.reload()]),
      smsAlerts: smsAlerts.reload,
      fields: fields.reload,
      officiating: () => Promise.all([matches.reload(), matchDetail.reload()]),
      transmissions: () => Promise.all([matches.reload(), matchDetail.reload()]),
      ranking: ranking.reload,
      statistics: () => Promise.all([playerStats.reload(), teamStats.reload()]),
      products: () => Promise.all([products.reload(), marketplace.reload()]),
      coupons: coupons.reload,
      orders: () => Promise.all([orders.reload(), orderDetail.reload()]),
      chat: () => Promise.all([rooms.reload(), roomMessages.reload()]),
      journal: journal.reload,
      moderation: () => Promise.all([rooms.reload(), moderation.reload()]),
      users: users.reload,
    };
    return loaders[currentView]?.();
  };

  const createProduct = async (payload) => {
    setBusyAction('product-create');
    try {
      await api.createProduct(token, payload);
      await products.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar produto.');
    } finally {
      setBusyAction('');
    }
  };

  const sendSmsAlert = async (payload) => {
    setBusyAction('sms-alert');
    try {
      await api.sendSmsAlert(token, payload);
      await smsAlerts.reload();
    } catch (error) {
      alert(error.message || 'Falha ao enviar alerta SMS.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const connectMercadoPago = async (teamId) => {
    setBusyAction('marketplace-connect');
    try {
      const response = await api.connectMercadoPago(token, { team_id: teamId });
      const url = response?.data?.authorization_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      await marketplace.reload();
    } catch (error) {
      alert(error.message || 'Falha ao iniciar conexão com Mercado Pago.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const uploadProductImage = async (file, productId = null) => {
    setBusyAction('product-upload');
    try {
      const response = await api.uploadImage(token, file, {
        context: 'products',
        entity_id: productId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar imagem do produto.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const createChampionship = async (payload) => {
    setBusyAction('championship-create');
    try {
      await api.createChampionship(token, payload);
      await championships.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar campeonato.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateChampionship = async (championshipId, payload) => {
    setBusyAction('championship-update');
    try {
      await api.updateChampionship(token, championshipId, payload);
      await championships.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar campeonato.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteChampionship = async (championshipId) => {
    if (!window.confirm('Excluir este campeonato?')) {
      return;
    }
    setBusyAction('championship-delete');
    try {
      await api.deleteChampionship(token, championshipId);
      await championships.reload();
    } catch (error) {
      alert(error.message || 'Falha ao excluir campeonato.');
    } finally {
      setBusyAction('');
    }
  };

  const updateProduct = async (productId, payload) => {
    setBusyAction('product-update');
    try {
      await api.updateProduct(token, productId, payload);
      await products.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar produto.');
    } finally {
      setBusyAction('');
    }
  };

  const createCoupon = async (payload) => {
    setBusyAction('coupon-create');
    try {
      await api.createCoupon(token, payload);
      await coupons.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar cupom.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateCoupon = async (couponId, payload) => {
    setBusyAction('coupon-update');
    try {
      await api.updateCoupon(token, couponId, payload);
      await coupons.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar cupom.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Remover este cupom?')) {
      return;
    }

    setBusyAction('coupon-delete');
    try {
      await api.deleteCoupon(token, couponId);
      await coupons.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover cupom.');
    } finally {
      setBusyAction('');
    }
  };

  const createJournalPost = async (payload) => {
    setBusyAction('journal-create');
    try {
      await api.createJournalPost(token, payload);
      await journal.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar materia.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateJournalPost = async (postId, payload) => {
    setBusyAction('journal-update');
    try {
      await api.updateJournalPost(token, postId, payload);
      await journal.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar materia.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteJournalPost = async (postId) => {
    if (!window.confirm('Arquivar esta materia do Jornal Perifa?')) {
      return;
    }

    setBusyAction('journal-delete');
    try {
      await api.deleteJournalPost(token, postId);
      await journal.reload();
    } catch (error) {
      alert(error.message || 'Falha ao arquivar materia.');
    } finally {
      setBusyAction('');
    }
  };

  const uploadJournalCover = async (file, postId = null) => {
    setBusyAction('journal-upload');
    try {
      const response = await api.uploadImage(token, file, {
        context: 'journal',
        entity_id: postId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar capa do Jornal Perifa.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteProduct = async (productId) => {
    setBusyAction('product-delete');
    try {
      await api.deleteProduct(token, productId);
      await products.reload();
    } catch (error) {
      alert(error.message || 'Falha ao excluir produto.');
    } finally {
      setBusyAction('');
    }
  };

  const updateOrder = async (orderId, payload) => {
    setBusyAction('order-update');
    try {
      await api.updateOrder(token, orderId, payload);
      await Promise.all([orders.reload(), orderDetail.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao atualizar pedido.');
    } finally {
      setBusyAction('');
    }
  };

  const cancelOrder = async (orderId) => {
    setBusyAction('order-cancel');
    try {
      await api.cancelOrder(token, orderId);
      await Promise.all([orders.reload(), orderDetail.reload(), products.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao cancelar pedido.');
    } finally {
      setBusyAction('');
    }
  };

  const sendMessage = async (roomId, payload) => {
    setBusyAction('chat');
    try {
      await api.sendChatMessage(token, roomId, payload);
      await roomMessages.reload();
      await rooms.reload();
    } catch (error) {
      alert(error.message || 'Falha ao enviar mensagem.');
    } finally {
      setBusyAction('');
    }
  };

  const applyModeration = async (roomId, payload) => {
    setBusyAction('moderation-apply');
    try {
      await api.applyChatModeration(token, roomId, payload);
      await Promise.all([moderation.reload(), rooms.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao aplicar moderação.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const removeRestriction = async (restrictionId) => {
    if (!window.confirm('Remover esta restrição?')) {
      return;
    }

    setBusyAction('moderation-remove');
    try {
      await api.removeChatRestriction(token, restrictionId);
      await moderation.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover restrição.');
    } finally {
      setBusyAction('');
    }
  };

  const startMatch = async (matchId) => {
    setBusyAction('start');
    try {
      await api.startMatch(token, matchId);
      await syncSportsViews();
    } catch (error) {
      alert(error.message || 'Falha ao iniciar partida.');
    } finally {
      setBusyAction('');
    }
  };

  const saveScore = async (matchId, payload) => {
    setBusyAction('score');
    try {
      await api.updateScore(token, matchId, payload);
      await syncSportsViews();
    } catch (error) {
      alert(error.message || 'Falha ao salvar placar.');
    } finally {
      setBusyAction('');
    }
  };

  const registerEvent = async (matchId, payload) => {
    setBusyAction('event');
    try {
      await api.registerMatchEvent(token, matchId, payload);
      await syncSportsViews();
    } catch (error) {
      alert(error.message || 'Falha ao registrar evento.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const finishMatch = async (matchId, payload) => {
    setBusyAction('finish');
    try {
      await api.finishMatch(token, matchId, payload);
      await syncSportsViews();
    } catch (error) {
      alert(error.message || 'Falha ao encerrar partida.');
    } finally {
      setBusyAction('');
    }
  };

  const createMatch = async (payload) => {
    setBusyAction('match-create');
    try {
      await api.createMatch(token, payload);
      await matches.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar jogo.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateMatch = async (matchId, payload) => {
    setBusyAction('match-update');
    try {
      await api.updateMatch(token, matchId, payload);
      await syncSportsViews();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar jogo.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const processMatchReminders = async () => {
    setBusyAction('match-reminders');
    try {
      const response = await api.processMatchReminders(token);
      const summary = response?.data || {};
      await matches.reload();
      alert(
        `Lembretes processados: ${summary.reminders_sent || 0}. ` +
        `Notificacoes: ${summary.notifications || 0}. ` +
        `Push: ${summary.pushes || 0}. SMS: ${summary.sms || 0}.`
      );
      return summary;
    } catch (error) {
      alert(error.message || 'Falha ao processar lembretes de jogos.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm('Remover este jogo?')) {
      return;
    }

    setBusyAction('match-delete');
    try {
      await api.deleteMatch(token, matchId);
      setSelectedMatchId(null);
      await matches.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover jogo.');
    } finally {
      setBusyAction('');
    }
  };

  const saveLineups = async (matchId, payload) => {
    setBusyAction('lineups');
    try {
      await api.saveLineups(token, matchId, payload);
      await matchDetail.reload();
    } catch (error) {
      alert(error.message || 'Falha ao salvar escalações.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const createField = async (payload) => {
    setBusyAction('field-create');
    try {
      await api.createField(token, payload);
      await Promise.all([fields.reload(), matches.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao criar campo.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const uploadFieldImage = async (file, fieldId = null) => {
    setBusyAction('field-upload');
    try {
      const response = await api.uploadImage(token, file, {
        context: 'fields',
        entity_id: fieldId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar imagem do campo.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateField = async (fieldId, payload) => {
    setBusyAction('field-update');
    try {
      await api.updateField(token, fieldId, payload);
      await Promise.all([fields.reload(), matches.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao atualizar campo.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Remover este campo?')) {
      return;
    }

    setBusyAction('field-delete');
    try {
      await api.deleteField(token, fieldId);
      await Promise.all([fields.reload(), matches.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao remover campo.');
    } finally {
      setBusyAction('');
    }
  };

  const saveOfficiating = async (matchId, payload) => {
    setBusyAction('officiating-update');
    try {
      await api.updateMatch(token, matchId, payload);
      await Promise.all([matches.reload(), matchDetail.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao salvar arbitragem.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const saveTransmission = async (matchId, payload) => {
    setBusyAction('transmission-update');
    try {
      await api.updateMatch(token, matchId, payload);
      await Promise.all([matches.reload(), matchDetail.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao salvar transmissão.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const uploadTransmissionVideo = async (file, matchId = null) => {
    setBusyAction('transmission-upload');
    try {
      const response = await api.uploadFile(token, file, {
        context: 'transmissions',
        entity_id: matchId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar video da transmissão.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const clearTransmission = async (matchId) => {
    if (!window.confirm('Remover a transmissão desta partida?')) {
      return;
    }

    setBusyAction('transmission-clear');
    try {
      await api.updateMatch(token, matchId, { transmission: null });
      await Promise.all([matches.reload(), matchDetail.reload()]);
    } catch (error) {
      alert(error.message || 'Falha ao remover transmissão.');
    } finally {
      setBusyAction('');
    }
  };

  const createUser = async (payload) => {
    setBusyAction('user-create');
    try {
      await api.createUser(token, payload);
      await users.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar usuário.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateUser = async (userId, payload) => {
    setBusyAction('user-update');
    try {
      await api.updateUser(token, userId, payload);
      await users.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar usuário.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Remover este usuário?')) {
      return;
    }

    setBusyAction('user-delete');
    try {
      await api.deleteUser(token, userId);
      await users.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover usuário.');
    } finally {
      setBusyAction('');
    }
  };

  const createTeam = async (payload) => {
    setBusyAction('team-create');
    try {
      await api.createTeam(token, payload);
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar time.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updateTeam = async (teamId, payload) => {
    setBusyAction('team-update');
    try {
      await api.updateTeam(token, teamId, payload);
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar time.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const transferTeamResponsibility = async (teamId, payload) => {
    setBusyAction('team-responsibility');
    try {
      await api.transferTeamResponsibility(token, teamId, payload);
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao resolver responsabilidade do time.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const uploadTeamLogo = async (file, teamId = null) => {
    setBusyAction('team-upload');
    try {
      const response = await api.uploadImage(token, file, {
        context: 'teams',
        entity_id: teamId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar logo do time.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Remover este time?')) {
      return;
    }

    setBusyAction('team-delete');
    try {
      await api.deleteTeam(token, teamId);
      await teams.reload();
      await players.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover time.');
    } finally {
      setBusyAction('');
    }
  };

  const createPlayer = async (payload) => {
    setBusyAction('player-create');
    try {
      await api.createPlayer(token, payload);
      await players.reload();
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao criar jogador.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const updatePlayer = async (playerId, payload) => {
    setBusyAction('player-update');
    try {
      await api.updatePlayer(token, playerId, payload);
      await players.reload();
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao atualizar jogador.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const uploadPlayerImage = async (file, playerId = null) => {
    setBusyAction('player-upload');
    try {
      const response = await api.uploadImage(token, file, {
        context: 'players',
        entity_id: playerId || '',
      });
      return response?.data?.url || '';
    } catch (error) {
      alert(error.message || 'Falha ao enviar imagem do jogador.');
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const deletePlayer = async (playerId) => {
    if (!window.confirm('Remover este jogador?')) {
      return;
    }

    setBusyAction('player-delete');
    try {
      await api.deletePlayer(token, playerId);
      await players.reload();
      await teams.reload();
    } catch (error) {
      alert(error.message || 'Falha ao remover jogador.');
    } finally {
      setBusyAction('');
    }
  };

  const goToPublicRoute = () => {
    window.history.replaceState(null, '', '/');
    setRouteMode('public');
  };

  const goToAdminRoute = () => {
    window.history.replaceState(null, '', '/admin');
    setRouteMode('app');
  };

  if (routeMode === 'not-found') {
    return <PerifaNotFoundGame onReturn={goToPublicRoute} />;
  }

  if (routeMode === 'public') {
    return (
      <PublicLandingPage
        onAdminAccess={() => {
          goToAdminRoute();
        }}
      />
    );
  }

  if (!token || !user) {
    return (
      <LoginScreen
        onSubmit={login}
        onRegister={register}
        onForgotPassword={forgotPassword}
        onResetPassword={resetPassword}
        loading={authLoading}
        error={authError}
      />
    );
  }

  return (
    <div className="admin-shell">
      <Sidebar currentView={currentView} onChangeView={setCurrentView} user={user} onLogout={logout} />
      <main className="admin-main">
        <Topbar
          title={pageMeta.title}
          description={pageMeta.description}
          onRefresh={refreshCurrentPage}
          refreshing={false}
        />

        {currentView === 'dashboard' && <DashboardPage loading={dashboard.loading} error={dashboard.error} data={dashboard.data?.data} />}
        {currentView === 'championships' && (
          <ChampionshipsPage
            loading={championships.loading}
            error={championships.error}
            data={championships.data?.data}
            onCreateChampionship={createChampionship}
            onUpdateChampionship={updateChampionship}
            onDeleteChampionship={deleteChampionship}
            saving={busyAction === 'championship-create' || busyAction === 'championship-update'}
            deleting={busyAction === 'championship-delete'}
            canManage={Boolean(access.championships?.manage)}
          />
        )}
        {currentView === 'teams' && (
          <TeamsPage
            loading={teams.loading || users.loading}
            error={teams.error || users.error}
            data={{ ...(teams.data?.data || {}), users: users.data?.data?.items || [] }}
            onCreateTeam={createTeam}
            onUpdateTeam={updateTeam}
            onTransferTeamResponsibility={transferTeamResponsibility}
            onDeleteTeam={deleteTeam}
            onUploadTeamLogo={uploadTeamLogo}
            canManage={Boolean(access.teams?.manage)}
            canCreate={Boolean(user?.roles?.some((role) => role === 'super_admin' || role === 'admin' || role === 'organizer'))}
            canOverrideResponsibility={Boolean(user?.roles?.some((role) => role === 'super_admin' || role === 'admin'))}
            creating={busyAction === 'team-create'}
            updating={busyAction === 'team-update'}
            transferringResponsibility={busyAction === 'team-responsibility'}
            uploadingLogo={busyAction === 'team-upload'}
            deleting={busyAction === 'team-delete'}
          />
        )}
        {currentView === 'players' && (
          <PlayersPage
            loading={players.loading || teams.loading}
            error={players.error || teams.error}
            data={{ ...(players.data?.data || {}), teams: teams.data?.data?.items || [] }}
            onCreatePlayer={createPlayer}
            onUpdatePlayer={updatePlayer}
            onDeletePlayer={deletePlayer}
            onUploadPlayerImage={uploadPlayerImage}
            canManage={Boolean(access.players?.manage)}
            creating={busyAction === 'player-create'}
            updating={busyAction === 'player-update'}
            uploading={busyAction === 'player-upload'}
            deleting={busyAction === 'player-delete'}
          />
        )}
        {currentView === 'matches' && (
          <MatchesPage
            loading={matches.loading || fields.loading || (Boolean(selectedMatchId) && matchDetail.loading)}
            error={matches.error || fields.error || matchDetail.error}
            data={{
              ...(matches.data?.data || {}),
              teams: teams.data?.data?.items || [],
              championships: championships.data?.data?.items || [],
              referees: matches.data?.data?.references?.referees || [],
              fields: fields.data?.data?.items || [],
              championshipTeams: matches.data?.data?.references?.championship_teams || [],
            }}
            selectedMatch={matchDetail.data?.data || null}
            allPlayers={players.data?.data?.items || []}
            onSelectMatch={setSelectedMatchId}
            onCreateMatch={createMatch}
            onUpdateMatch={updateMatch}
            onProcessReminders={processMatchReminders}
            onDeleteMatch={deleteMatch}
            onStartMatch={startMatch}
            onFinishMatch={finishMatch}
            onUpdateScore={saveScore}
            onRegisterEvent={registerEvent}
            onSaveLineups={saveLineups}
            onCreateField={createField}
            onUpdateField={updateField}
            onDeleteField={deleteField}
            matchBusy={busyAction}
            canManageSchedule={Boolean(user?.roles?.some((role) => role === 'super_admin' || role === 'admin' || role === 'organizer'))}
            canManageLineups={Boolean(
              user?.roles?.some((role) => role === 'super_admin' || role === 'admin' || role === 'organizer' || role === 'team_owner')
              || user?.team_scope_ids?.length
            )}
          />
        )}
        {currentView === 'smsAlerts' && (
          <SmsAlertsPage
            loading={smsAlerts.loading || championships.loading || teams.loading || matches.loading}
            error={smsAlerts.error || championships.error || teams.error || matches.error}
            data={smsAlerts.data?.data}
            championships={championships.data?.data?.items || []}
            teams={teams.data?.data?.items || []}
            matches={matches.data?.data?.items || []}
            onSendAlert={sendSmsAlert}
            sending={busyAction === 'sms-alert'}
            canManage={Boolean(user?.roles?.some((role) => role === 'super_admin' || role === 'admin' || role === 'organizer'))}
          />
        )}
        {currentView === 'fields' && (
          <FieldsPage
            loading={fields.loading}
            error={fields.error}
            data={fields.data?.data}
            onCreateField={createField}
            onUpdateField={updateField}
            onDeleteField={deleteField}
            onUploadFieldImage={uploadFieldImage}
            saving={busyAction === 'field-create' || busyAction === 'field-update'}
            uploading={busyAction === 'field-upload'}
            canManage={Boolean(access.matches?.manage)}
          />
        )}
        {currentView === 'officiating' && (
          <OfficiatingPage
            loading={matches.loading || (Boolean(selectedMatchId) && matchDetail.loading)}
            error={matches.error || matchDetail.error}
            matches={matches.data?.data?.items || []}
            selectedMatch={matchDetail.data?.data || null}
            referees={matches.data?.data?.references?.referees || []}
            onSelectMatch={setSelectedMatchId}
            onSave={saveOfficiating}
            saving={busyAction === 'officiating-update'}
            canManage={Boolean(access.matches?.manage)}
          />
        )}
        {currentView === 'transmissions' && (
          <TransmissionsPage
            loading={matches.loading || (Boolean(selectedMatchId) && matchDetail.loading)}
            error={matches.error || matchDetail.error}
            matches={matches.data?.data?.items || []}
            selectedMatch={matchDetail.data?.data || null}
            onSelectMatch={setSelectedMatchId}
            onSave={saveTransmission}
            onClear={clearTransmission}
            onUploadVideo={uploadTransmissionVideo}
            saving={busyAction === 'transmission-update' || busyAction === 'transmission-clear'}
            uploading={busyAction === 'transmission-upload'}
            canManage={Boolean(access.matches?.manage)}
          />
        )}
        {currentView === 'ranking' && (
          <RankingPage
            loading={ranking.loading || championships.loading}
            error={ranking.error || championships.error}
            data={ranking.data?.data}
            championships={championships.data?.data?.items || []}
            selectedChampionshipId={selectedChampionshipId}
            onSelectChampionship={setSelectedChampionshipId}
          />
        )}
        {currentView === 'statistics' && (
          <StatisticsPage
            loading={playerStats.loading || teamStats.loading || championships.loading}
            error={playerStats.error || teamStats.error || championships.error}
            playerData={playerStats.data?.data}
            teamData={teamStats.data?.data}
            championships={championships.data?.data?.items || []}
            selectedChampionshipId={selectedChampionshipId}
            onSelectChampionship={setSelectedChampionshipId}
          />
        )}
        {currentView === 'products' && (
          <ProductsPage
            loading={products.loading}
            error={products.error}
            data={{ ...(products.data?.data || {}), teams: teams.data?.data?.items || [], filters: productFilters, marketplace: marketplace.data?.data || null }}
            onCreateProduct={createProduct}
            onUpdateProduct={updateProduct}
            onDeleteProduct={deleteProduct}
            onUploadProductImage={uploadProductImage}
            onConnectMercadoPago={connectMercadoPago}
            onFilter={setProductFilters}
            creating={busyAction === 'product-create'}
            updating={busyAction === 'product-update'}
            uploading={busyAction === 'product-upload'}
            deleting={busyAction === 'product-delete'}
            connectingMarketplace={busyAction === 'marketplace-connect'}
            canManage={Boolean(access.products?.manage)}
          />
        )}
        {currentView === 'orders' && (
          <OrdersPage
            loading={orders.loading || (Boolean(selectedOrderId) && orderDetail.loading)}
            error={orders.error || orderDetail.error}
            data={{ ...(orders.data?.data || {}), filters: orderFilters }}
            detail={orderDetail.data?.data || null}
            selectedOrderId={selectedOrderId}
            onSelectOrder={setSelectedOrderId}
            onUpdateOrder={updateOrder}
            onCancelOrder={cancelOrder}
            onFilter={setOrderFilters}
            updating={busyAction === 'order-update'}
            cancelling={busyAction === 'order-cancel'}
          />
        )}
        {currentView === 'coupons' && (
          <CouponsPage
            loading={coupons.loading}
            error={coupons.error}
            data={coupons.data?.data}
            filters={couponFilters}
            onFilter={setCouponFilters}
            onCreateCoupon={createCoupon}
            onUpdateCoupon={updateCoupon}
            onDeleteCoupon={deleteCoupon}
            saving={busyAction === 'coupon-create' || busyAction === 'coupon-update'}
            deleting={busyAction === 'coupon-delete'}
            canManage={Boolean(access.products?.manage)}
          />
        )}
        {currentView === 'chat' && <ChatPage loading={rooms.loading || roomMessages.loading} error={rooms.error || roomMessages.error} roomsData={rooms.data?.data} messagesData={roomMessages.data?.data} selectedRoomId={selectedRoomId} onSelectRoom={setSelectedRoomId} onSendMessage={sendMessage} sending={busyAction === 'chat'} />}
        {currentView === 'journal' && (
          <JournalPage
            loading={journal.loading || championships.loading}
            error={journal.error || championships.error}
            data={journal.data?.data}
            championshipsData={championships.data?.data}
            onCreatePost={createJournalPost}
            onUpdatePost={updateJournalPost}
            onDeletePost={deleteJournalPost}
            onUploadCover={uploadJournalCover}
            saving={busyAction === 'journal-create' || busyAction === 'journal-update'}
            deleting={busyAction === 'journal-delete'}
            uploading={busyAction === 'journal-upload'}
            canManage={Boolean(access.dashboard?.manage)}
          />
        )}
        {currentView === 'moderation' && (
          <ModerationPage
            loading={rooms.loading || moderation.loading || users.loading}
            error={rooms.error || moderation.error || users.error}
            rooms={rooms.data?.data?.items || []}
            selectedRoomId={selectedModerationRoomId}
            onSelectRoom={setSelectedModerationRoomId}
            moderationData={moderation.data?.data || { items: [] }}
            users={users.data?.data?.items || []}
            onApplyModeration={applyModeration}
            onRemoveRestriction={removeRestriction}
            busy={busyAction === 'moderation-apply' || busyAction === 'moderation-remove'}
            canManage={Boolean(access.chat?.manage)}
          />
        )}
        {currentView === 'users' && (
          <UsersPage
            loading={users.loading || auditLogs.loading}
            error={users.error || auditLogs.error}
            data={{ ...(users.data?.data || {}), audit: auditLogs.data?.data?.items || [], access }}
            onCreateUser={createUser}
            onUpdateUser={updateUser}
            onDeleteUser={deleteUser}
            creating={busyAction === 'user-create'}
            updating={busyAction === 'user-update'}
            deleting={busyAction === 'user-delete'}
            canManage={Boolean(access.users?.manage)}
            manageableRoles={manageableRoles}
          />
        )}
      </main>
    </div>
  );
}

export default App;
